import React, { useCallback, useState } from 'react';
import { Form, Popover } from 'antd';
import { ProjectDistributionList } from '../../../models/ProjectDistributionList';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import classNames from 'classnames';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import { Button, Drawer, Dropdown, Menu } from '@prio365/prio365-react-library';
import MenuItem from '@prio365/prio365-react-library/lib/Menu/components/MenuItem';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import ProjectDistributionListDuplicateModal from './ProjectDistributionListDuplicateModal';
import ProjectDistributionListForm from './ProjectDistributionListForm';
import { ProjectId } from '../../../models/Types';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    overflow: 'hidden',
    flex: 1,
  },
  row: {
    cursor: 'pointer',
    '& button': {
      display: 'none',
    },
    '&:hover button': {
      display: 'block',
    },
  },
  table: {
    borderRadius: '4px',
  },
  tableCell: {
    display: 'contents',
    '& > div': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      marginLeft: '10px',
    },
  },
  mailPopover: {
    color: theme.colors.application.typography.default,
    maxHeight: '150px',
    overflowY: 'auto',
  },
  contextMenu: {
    background: theme.colors.application.background.default,
    padding: 0,
    fontSize: theme.font.fontSize.regular,
    '& li': {
      marginLeft: 0,
    },
  },
  dropdown: {
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
  },
  actionRow: {
    marginBottom: theme.old.spacing.unit(2),
  },
  fullWidth: {
    width: '100%',
  },
  danger: {
    color: theme.old.palette.chromaticPalette.red,
    '&:hover': {
      backgroundColor: theme.old.palette.chromaticPalette.red,
      color: theme.old.palette.chromaticPalette.white,
    },
    '& > .prio-button-icon': {
      color: theme.old.palette.chromaticPalette.red,
    },
  },
  drawerContent: {
    paddingBottom: 0,
  },
}));

interface ProjectDistributionListTableProps {
  className?: string;
  distributionLists: ProjectDistributionList[];
  projectId: ProjectId;
  onRowClick?: (
    item: ProjectDistributionList,
    index: number,
    selectedItems: ProjectDistributionList[]
  ) => VirtualListItemOnRowProps<null, null>;
  selectedTableItems?: ProjectDistributionList[];
  setSelectedTableItems?: (items: ProjectDistributionList[]) => void;
  showBulkActions?: boolean;
  handleDeleteDistributionList?: (
    distributionList: ProjectDistributionList[]
  ) => void;
  showCreateButton?: boolean;
  showDeleteButton?: boolean;
}

export const ProjectDistributionListTable: React.FC<
  ProjectDistributionListTableProps
> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const {
    className,
    distributionLists,
    projectId,
    onRowClick,
    selectedTableItems,
    setSelectedTableItems,
    showBulkActions = true,
    handleDeleteDistributionList,
    showCreateButton = false,
    showDeleteButton = true,
  } = props;

  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { getContactById } = useContactsContext();

  const existingNames = distributionLists.map((item) => item.name);

  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);

  const [selectedDistributionList, setSelectedDistributionList] =
    useState<ProjectDistributionList | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [form] = Form.useForm();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleCreateProjectDistributionList = () => {
    setSelectedDistributionList(null);
    setIsDrawerOpen(true);
  };

  const handleEditDistributionList = useCallback(
    (item: ProjectDistributionList) => {
      setSelectedDistributionList(item);
      setIsDrawerOpen(true);
    },
    []
  );

  const handleClose = () => {
    setSelectedDistributionList(null);
    setIsDrawerOpen(false);
    form.resetFields();
  };

  const handleRowClick = useCallback(
    (
      item: ProjectDistributionList,
      index: number,
      selectedItems: ProjectDistributionList[]
    ) => {
      if (onRowClick) {
        return onRowClick(item, index, selectedItems);
      }
      return {
        onClick: (event) => {
          event.preventDefault();
          handleEditDistributionList(item);
        },
      };
    },
    [onRowClick, handleEditDistributionList]
  );
  //#endregion

  const menu = useCallback(
    (item: ProjectDistributionList) => {
      return (
        <Menu className={classes.contextMenu} selectedItemId="">
          {!showDeleteButton && (
            <MenuItem
              id="0"
              onClick={(e) => {
                handleEditDistributionList(item);
              }}
              icon={['fal', 'edit']}
              label={t('common:actions.edit')}
            ></MenuItem>
          )}
          <MenuItem
            id="1"
            onClick={(e) => {
              setIsDuplicateModalVisible(true);
              setSelectedDistributionList(item);
            }}
            icon={['fal', 'copy']}
            label={t('projects:projectDistributionList.table.contextMenu.copy')}
          ></MenuItem>
          {showDeleteButton && (
            <MenuItem
              id="2"
              onClick={(e) => {
                handleDeleteDistributionList([item]);
              }}
              icon={['fal', 'trash']}
              label={t(
                'projects:projectDistributionList.table.contextMenu.delete'
              )}
            ></MenuItem>
          )}
        </Menu>
      );
    },
    [
      t,
      classes.contextMenu,
      handleDeleteDistributionList,
      showDeleteButton,
      handleEditDistributionList,
    ]
  );

  const columns: Column<ProjectDistributionList>[] = [
    {
      title: t('projects:projectDistributionList.table.name'),
      id: 'name',
      accessor: 'name',
      sortingFn: (a, b) => a.name.localeCompare(b.name),
      Cell: (CellProps) => <div> {CellProps.value}</div>,
      width: 20,
      className: classes.tableCell,
    },
    {
      title: t(
        'projects:projectDistributionList.table.projectDistributionContacts'
      ),
      id: 'projectDistributionContacts',
      accessor: 'projectDistributionContacts',
      Cell: (CellProps) => {
        const joinedValue = CellProps.value
          .map((item) =>
            getContactById(item?.contactId)
              ? `${getContactById(item?.contactId).firstName} ${
                  getContactById(item?.contactId).lastName
                }`
              : item.eMail
          )
          .join('; ');

        return (
          <Popover
            content={
              <div className={classes.mailPopover}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: theme.old.spacing.unit(0.5),
                  }}
                >
                  {t('projects:projectDistributionList.form.contacts.contacts')}
                </div>
                <ul>
                  {CellProps.value.map((item) => (
                    <li key={item.eMail}>
                      {getContactById(item?.contactId)
                        ? `${getContactById(item?.contactId).firstName} ${
                            getContactById(item?.contactId).lastName
                          }  (${item.eMail})`
                        : item.eMail}
                    </li>
                  ))}
                </ul>
              </div>
            }
          >
            <div>{joinedValue}</div>
          </Popover>
        );
      },
      width: 72,
      className: classes.tableCell,
    },
    {
      title: '',
      id: 'contextMenu',
      accessor: 'projectDistributionContacts',
      alignSelf: true,
      Cell: ({ originalData }) => {
        return (
          <Dropdown
            overlay={menu(originalData)}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName={classes.dropdown}
            onOverlayClick={(e) => e.stopPropagation()}
          >
            <Button
              iconProp={['fal', 'ellipsis-v']}
              type="default"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Dropdown>
        );
      },
      width: 8,
    },
  ];

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      {showCreateButton && (
        <Flex.Row className={classes.actionRow}>
          <Button
            onClick={handleCreateProjectDistributionList}
            iconProp={['fal', 'plus']}
          >
            <span>
              {t('projects:navigationBar.createProjectDistributionList')}
            </span>
          </Button>
        </Flex.Row>
      )}
      <FilterContextVirtualTable
        id="projectDistributionListTable"
        className={classes.table}
        classNameTableRow={classes.row}
        columns={columns}
        data={distributionLists.sort((a, b) => a.name.localeCompare(b.name))}
        rowsAreSelectable
        selectedItems={selectedTableItems}
        onSelectionChange={setSelectedTableItems}
        resizable="relative"
        onRow={handleRowClick}
        actionBarButtons={
          showBulkActions
            ? [
                {
                  children: t('common:actions.delete'),
                  iconProp: ['fal', 'trash'],
                  onClick: () => handleDeleteDistributionList(selectedTableItems),
                },
              ]
            : null
        }
      />
      <ProjectDistributionListDuplicateModal
        isModalVisible={isDuplicateModalVisible}
        setIsModalVisible={setIsDuplicateModalVisible}
        selectedDistributionList={selectedDistributionList}
        existingNames={existingNames}
      />
      <Drawer
        title={
          selectedDistributionList
            ? t('projects:projectDistributionList.drawerTitle.edit')
            : t('projects:projectDistributionList.drawerTitle.create')
        }
        visible={isDrawerOpen}
        footer={
          <Flex.Row className={classes.fullWidth}>
            {selectedDistributionList && showDeleteButton && (
              <Button
                className={classes.danger}
                type="default"
                onClick={() => {
                  handleDeleteDistributionList([selectedDistributionList]);
                  handleClose();
                }}
              >
                {t('common:actions.delete')}
              </Button>
            )}
            <Flex.Row
              flex={1}
              justifyContent="flex-end"
              childrenGap={theme.old.spacing.unit(2)}
            >
              <Button type="default" onClick={handleClose}>
                {t('common:actions.cancel')}
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                onClick={() => form.submit()}
                disabled={isSaveButtonDisabled}
              >
                {t('common:actions.save')}
              </Button>
            </Flex.Row>
          </Flex.Row>
        }
        onClose={handleClose}
        contentClassName={classes.drawerContent}
      >
        <ProjectDistributionListForm
          initialValues={selectedDistributionList}
          openedDistributionList={selectedDistributionList}
          projectId={projectId}
          form={form}
          closeDrawer={handleClose}
          setIsSaveButtonDisabled={setIsSaveButtonDisabled}
        />
      </Drawer>
    </Flex.Column>
  );
};

export default ProjectDistributionListTable;
